import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Dark by default, light available.
 *
 * The `dark` class is applied in `index.html` before React mounts, so a reload
 * does not flash white at an operator working in a dark room. This provider
 * only handles the toggle and the persistence; the initial paint is already
 * settled by the time it runs, which is why it reads the DOM rather than
 * `localStorage` for its initial state.
 */

const ThemeContext = createContext(null);

/** Keep in step with the inline script in index.html. */
const STORAGE_KEY = 'frt-admin-theme';

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Private browsing can refuse writes. The theme still applies for this
      // session; only the persistence is lost, which is not worth an error.
    }
  }, [theme]);

  const toggle = useCallback(
    () => setTheme((current) => (current === 'dark' ? 'light' : 'dark')),
    [],
  );

  const value = useMemo(
    () => ({ theme, setTheme, toggle, isDark: theme === 'dark' }),
    [theme, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
};
